// manufacturing/ConvertToPurchase.jsx - COMPLETE FIXED VERSION
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip, Modal, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Select from 'react-select';
import {
    Search, RotateCcw, Eye, Printer, ChevronUp,
    ShoppingBag, Calendar, Package, DollarSign, TrendingUp, CheckCircle, AlertCircle,
} from 'feather-icons-react/build/IconComponents';
import { manufacturingActions } from "../../core/action/manufacturingAction";
import { supplierAPI } from "../../core/action/api";
import ImageWithBasePath from '../../core/img/imagewithbasebath';

const MySwal = withReactContent(Swal);

const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount || 0);

const ConvertToPurchase = () => {
    const dispatch = useDispatch();
    const reduxData = useSelector((state) => state.toggle_header);

    const [productions, setProductions] = useState([]);
    const [convertedProductions, setConvertedProductions] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showConvertModal, setShowConvertModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedProduction, setSelectedProduction] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [editableItems, setEditableItems] = useState([]);
    const [taxPercent, setTaxPercent] = useState('');
    const [selectedProductionForConvert, setSelectedProductionForConvert] = useState(null);

    const [formData, setFormData] = useState({
        production_id: '',
        supplier_id: '',
        purchase_date: new Date().toISOString().split('T')[0],
        notes: '',
    });
    const [errors, setErrors] = useState({});

    const [filters, setFilters] = useState({ search: '', from_date: '', to_date: '' });
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });

    const canConvert = manufacturingActions.hasManufacturingPermission('convert-production');

    // Load unconverted productions
    const loadUnconvertedProductions = useCallback(async (page = 1, currentFilters = filters) => {
        setLoading(true);
        try {
            const result = await manufacturingActions.getUnconvertedProductions({
                page, limit: pagination.limit, ...currentFilters,
            });
            if (result.success) {
                setProductions(result.data || []);
                setPagination(prev => ({
                    ...prev, page,
                    total: result.total || 0,
                    totalPages: result.totalPages || 1,
                }));
            } else {
                toast.error(result.message || 'Failed to load productions');
            }
        } catch (error) {
            toast.error('Failed to load productions');
        } finally {
            setLoading(false);
        }
    }, [pagination.limit]);

    // Load already converted productions
    const loadConvertedProductions = async () => {
        try {
            const result = await manufacturingActions.getProductions({ converted_to_purchase: 1, limit: 50 });
            if (result.success) setConvertedProductions(result.data || []);
        } catch (error) {
            console.error('Error loading converted productions:', error);
        }
    };

    // Load suppliers
    const loadSuppliers = async () => {
        try {
            const result = await supplierAPI.getAll({ limit: 1000 });
            if (result?.success) setSuppliers(result.data || []);
        } catch (error) {
            console.error('Error loading suppliers:', error);
        }
    };

    useEffect(() => {
        loadUnconvertedProductions(1, filters);
        loadConvertedProductions();
        loadSuppliers();
    }, []);

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        const updated = { ...filters, [key]: value };
        setFilters(updated);
        loadUnconvertedProductions(1, updated);
    };

    // Select production for conversion - FIXED
    const handleProductionSelect = useCallback(async (selected) => {
        if (!selected) {
            setSelectedProduction(null);
            setSelectedProductionForConvert(null);
            setEditableItems([]);
            setFormData(prev => ({ ...prev, production_id: '' }));
            return;
        }

        try {
            setLoading(true);
            const result = await manufacturingActions.getProduction(selected.value);
            if (result.success) {
                const prod = result.data;
                setSelectedProduction(prod);
                setSelectedProductionForConvert(prod);
                setFormData(prev => ({ ...prev, production_id: prod.id }));

                // Initialize editable items from finished goods
                const items = (prod.finished_goods || []).map(g => ({
                    product_id: g.product_id,
                    product_name: g.product_name,
                    sku: g.sku,
                    unit: g.unit || 'PCS',
                    quantity: parseFloat(g.quantity_produced) || 0,
                    unit_price: parseFloat(g.unit_cost) || 0,
                    discount: 0,
                    tax_percent: '',
                }));
                console.log(selectedProductionForConvert)
                setEditableItems(items);
            } else {
                toast.error('Failed to load production details');
            }
        } catch (error) {
            console.error('Error loading production:', error);
            toast.error('Failed to load production details');
        } finally {
            setLoading(false);
        }
    }, []);

    // Open convert modal from table row - FIXED
    const openConvertForProduction = async (prod) => {
        setShowConvertModal(true);
        // Create a selected option object
        const selectedOption = { value: prod.id, label: `${prod.production_no} — ${prod.batch_no}` };
        await handleProductionSelect(selectedOption);
    };

    // Update editable item
    const updateItem = (idx, field, value) => {
        setEditableItems(prev => {
            const rows = [...prev];
            rows[idx] = { ...rows[idx], [field]: parseFloat(value) || 0 };
            return rows;
        });
    };

    // Calculate totals
    const computedItems = editableItems.map(item => {
        const qty = parseFloat(item.quantity) || 0;
        const unit_price = parseFloat(item.unit_price) || 0;
        const discount = parseFloat(item.discount) || 0;
        const tax_pct = item.tax_percent !== '' ? parseFloat(item.tax_percent) : (parseFloat(taxPercent) || 0);
        const line_sub = (qty * unit_price) - discount;
        const line_tax = line_sub * tax_pct / 100;
        const line_total = line_sub + line_tax;
        return { ...item, tax_percent_used: tax_pct, line_sub, line_tax, line_total };
    });

    const subtotal = computedItems.reduce((s, r) => s + r.line_sub, 0);
    const totalTax = computedItems.reduce((s, r) => s + r.line_tax, 0);
    const grandTotal = subtotal + totalTax;

    // Validate form
    const validateForm = () => {
        const errs = {};
        if (!formData.production_id) errs.production_id = 'Please select a production';
        if (editableItems.length === 0) errs.items = 'No items to convert';
        if (editableItems.some(i => parseFloat(i.quantity) <= 0)) errs.items = 'All item quantities must be > 0';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // Submit conversion
    const handleConvert = async () => {
        if (!validateForm()) {
            toast.error('Please fix the form errors');
            return;
        }

        const confirm = await MySwal.fire({
            title: 'Convert to Purchase?',
            html: `<p>Production <strong>${selectedProduction?.production_no}</strong> will be converted.</p>
                   <p>Grand Total: <strong>${formatCurrency(grandTotal)}</strong></p>
                   <p class="text-warning">This will transfer finished goods to warehouse stock.</p>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            confirmButtonText: 'Yes, Convert!',
        });
        if (!confirm.isConfirmed) return;

        setSubmitting(true);
        try {
            const payload = {
                production_id: formData.production_id,
                supplier_id: formData.supplier_id || undefined,
                purchase_date: formData.purchase_date,
                notes: formData.notes,
                tax_percent: taxPercent !== '' ? parseFloat(taxPercent) : undefined,
                items: computedItems.map(i => ({
                    product_id: i.product_id,
                    quantity: parseFloat(i.quantity),
                    unit_price: parseFloat(i.unit_price),
                    discount: parseFloat(i.discount) || 0,
                    tax_percent: i.tax_percent_used,
                })),
            };

            const result = await manufacturingActions.convertToPurchase(payload);
            if (result.success) {
                MySwal.fire({
                    icon: 'success',
                    title: 'Converted Successfully!',
                    html: `<p><strong>Purchase Order:</strong> ${result.data.purchase_reference_no}</p>
                           <p><strong>Amount:</strong> ${formatCurrency(result.data.grand_total)}</p>`,
                    timer: 3000,
                });
                resetForm();
                setShowConvertModal(false);
                loadUnconvertedProductions(1, filters);
                loadConvertedProductions();
            } else {
                toast.error(result.message || 'Failed to convert production');
            }
        } catch (err) {
            console.error('Conversion error:', err);
            toast.error(err.message || 'Failed to convert production');
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            production_id: '',
            supplier_id: '',
            purchase_date: new Date().toISOString().split('T')[0],
            notes: ''
        });
        setSelectedProduction(null);
        setSelectedProductionForConvert(null);
        setEditableItems([]);
        setTaxPercent('');
        setErrors({});
    };

    const handleViewProduction = async (prod) => {
        try {
            setLoading(true);
            const result = await manufacturingActions.getProduction(prod.id);
            if (result.success) {
                setSelectedProduction(result.data);
                setShowViewModal(true);
            } else {
                toast.error('Failed to load production details');
            }
        } catch (error) {
            toast.error('Failed to load production details');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const map = {
            completed: <span className="badge bg-success"><CheckCircle size={11} className="me-1" />COMPLETED</span>,
            draft: <span className="badge bg-warning">DRAFT</span>,
            cancelled: <span className="badge bg-danger">CANCELLED</span>
        };
        return map[status] || <span className="badge bg-secondary">{(status || 'N/A').toUpperCase()}</span>;
    };

    // Production options for dropdown - FIXED to include all unconverted productions
    const productionOptions = productions.map(p => ({
        value: p.id,
        label: `${p.production_no} — ${p.batch_no} (${formatDate(p.production_date)}) — ${formatCurrency(p.total_production_cost || 0)}`,
    }));

    const supplierOptions = suppliers.map(s => ({
        value: s.id,
        label: `${s.name}${s.gst_no ? ` (${s.gst_no})` : ''}`,
    }));

    const totals = {
        totalProductions: pagination.total,
        totalValue: productions.reduce((s, p) => s + parseFloat(p.total_production_cost || 0), 0),
        totalConverted: convertedProductions.length,
        totalConvertedValue: convertedProductions.reduce((s, p) => s + parseFloat(p.total_production_cost || 0), 0),
    };

    const renderTooltip = (props, text) => <Tooltip id={`tt-${text}`} {...props}>{text}</Tooltip>;

    const Pagination = () => {
        if (pagination.totalPages <= 1) return null;
        const { page, totalPages, total, limit } = pagination;
        const from = (page - 1) * limit + 1;
        const to = Math.min(page * limit, total);
        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">Showing {from}–{to} of {total}</div>
                <nav>
                    <ul className="pagination">
                        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => loadUnconvertedProductions(page - 1, filters)}>Previous</button>
                        </li>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (page <= 3) {
                                pageNum = i + 1;
                            } else if (page >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                            } else {
                                pageNum = page - 2 + i;
                            }
                            return (
                                <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                                    <button className="page-link" onClick={() => loadUnconvertedProductions(pageNum, filters)}>{pageNum}</button>
                                </li>
                            );
                        })}
                        <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => loadUnconvertedProductions(page + 1, filters)}>Next</button>
                        </li>
                    </ul>
                </nav>
            </div>
        );
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Convert to Purchase</h4>
                        <h6>Convert completed productions to purchase orders</h6>
                    </div>
                    <ul className="table-top-head">
                        {[['PDF', 'pdf'], ['Excel', 'excel']].map(([l, i]) => (
                            <li key={l}>
                                <OverlayTrigger placement="top" overlay={(p) => renderTooltip(p, l)}>
                                    <Link to="#"><ImageWithBasePath src={`assets/img/icons/${i}.svg`} alt={l} /></Link>
                                </OverlayTrigger>
                            </li>
                        ))}
                        <li>
                            <OverlayTrigger placement="top" overlay={(p) => renderTooltip(p, 'Print')}>
                                <Link onClick={() => window.print()}><Printer /></Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(p) => renderTooltip(p, 'Refresh')}>
                                <Link onClick={() => loadUnconvertedProductions(1, filters)}><RotateCcw /></Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(p) => renderTooltip(p, 'Collapse')}>
                                <Link className={reduxData ? 'active' : ''} onClick={() => dispatch(setToogleHeader(!reduxData))}><ChevronUp /></Link>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>

                {/* Stat Cards */}
                <div className="row">
                    {[
                        { label: 'Ready to Convert', value: totals.totalProductions, icon: <Package size={24} className="text-primary" />, bg: 'bg-primary' },
                        { label: 'Already Converted', value: totals.totalConverted, icon: <CheckCircle size={24} className="text-success" />, bg: 'bg-success' },
                        { label: 'Pending Value', value: formatCurrency(totals.totalValue), icon: <DollarSign size={24} className="text-info" />, bg: 'bg-info' },
                        { label: 'Converted Value', value: formatCurrency(totals.totalConvertedValue), icon: <ShoppingBag size={24} className="text-warning" />, bg: 'bg-warning' },
                    ].map(({ label, value, icon, bg }) => (
                        <div key={label} className="col-lg-3 col-md-6">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div className={`${bg} bg-opacity-10 p-3 rounded me-3`}>{icon}</div>
                                        <div>
                                            <p className="text-muted mb-1">{label}</p>
                                            <h3 className="mb-0">{value}</h3>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-lg-5">
                                <label>Search</label>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Production no, batch no..."
                                        value={filters.search}
                                        onChange={e => handleFilterChange('search', e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && loadUnconvertedProductions(1, filters)}
                                    />
                                    <button className="btn btn-primary" onClick={() => loadUnconvertedProductions(1, filters)}>
                                        <Search size={16} />
                                    </button>
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <label>From Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={filters.from_date}
                                    onChange={e => handleFilterChange('from_date', e.target.value)}
                                />
                            </div>
                            <div className="col-lg-2">
                                <label>To Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={filters.to_date}
                                    onChange={e => handleFilterChange('to_date', e.target.value)}
                                />
                            </div>
                            <div className="col-lg-1">
                                <label>&nbsp;</label>
                                <button
                                    className="btn btn-secondary w-100"
                                    onClick={() => {
                                        const empty = { search: '', from_date: '', to_date: '' };
                                        setFilters(empty);
                                        loadUnconvertedProductions(1, empty);
                                    }}
                                >
                                    Clear
                                </button>
                            </div>
                            <div className="col-lg-2">
                                <label>&nbsp;</label>
                                {canConvert && (
                                    <button
                                        className="btn btn-success w-100"
                                        onClick={() => setShowConvertModal(true)}
                                        disabled={productions.length === 0}
                                    >
                                        <TrendingUp size={16} className="me-2" />
                                        Convert Production
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Productions Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status" />
                            </div>
                        ) : productions.length === 0 ? (
                            <div className="text-center py-5">
                                <CheckCircle size={48} className="text-muted mb-3" />
                                <h5>No productions ready to convert</h5>
                                <p className="text-muted">All productions have been converted</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th>Production No</th>
                                            <th>Batch No</th>
                                            <th>Date</th>
                                            <th>Finished Goods</th>
                                            <th>Total Qty</th>
                                            <th>Total Cost</th>
                                            <th>Status</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {productions.map(prod => (
                                            <tr key={prod.id}>
                                                <td><strong>{prod.production_no}</strong></td>
                                                <td><code>{prod.batch_no}</code></td>
                                                <td>
                                                    <Calendar size={14} className="me-1 text-muted" />
                                                    {formatDate(prod.production_date)}
                                                </td>
                                                <td>{prod.finished_goods_count || 0} items</td>
                                                <td>{parseFloat(prod.total_quantity || 0).toLocaleString('en-IN')}</td>
                                                <td className="fw-bold text-success">{formatCurrency(prod.total_production_cost)}</td>
                                                <td>{getStatusBadge(prod.status)}</td>
                                                <td className="text-center">
                                                    <button
                                                        className="btn btn-sm btn-outline-info me-1"
                                                        onClick={() => handleViewProduction(prod)}
                                                        title="View Details"
                                                    >
                                                        <Eye size={14} />
                                                    </button>
                                                    {canConvert && (
                                                        <button
                                                            className="btn btn-sm btn-success"
                                                            onClick={() => openConvertForProduction(prod)}
                                                            title="Convert to Purchase"
                                                        >
                                                            <ShoppingBag size={14} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <Pagination />
                    </div>
                </div>

                {/* Recently Converted */}
                {convertedProductions.length > 0 && (
                    <div className="card mt-4">
                        <div className="card-header">
                            <h5 className="mb-0">
                                <CheckCircle size={18} className="me-2 text-success" />
                                Recently Converted
                            </h5>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Production No</th>
                                            <th>Batch No</th>
                                            <th>Date</th>
                                            <th>Value</th>
                                            <th>Purchase Order</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {convertedProductions.slice(0, 5).map(p => (
                                            <tr key={p.id}>
                                                <td>{p.production_no}</td>
                                                <td><code>{p.batch_no}</code></td>
                                                <td>{formatDate(p.production_date)}</td>
                                                <td className="text-success">{formatCurrency(p.total_production_cost)}</td>
                                                <td>
                                                    <span className="badge bg-success">{p.purchase_invoice_no || 'N/A'}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Convert Modal - FIXED */}
            <Modal show={showConvertModal} onHide={() => { resetForm(); setShowConvertModal(false); }} size="xl" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <ShoppingBag size={18} className="me-2" />
                        Convert Production to Purchase
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row mb-4">
                        <div className="col-md-12 mb-3">
                            <label className="form-label">Select Production *</label>
                            <Select
                                options={productionOptions}
                                onChange={handleProductionSelect}
                                placeholder="Choose production to convert"
                                isDisabled={submitting}
                                className={errors.production_id ? 'is-invalid' : ''}
                                value={productionOptions.find(opt => opt.value === formData.production_id)}
                            />
                            {errors.production_id && (
                                <div className="text-danger small mt-1">{errors.production_id}</div>
                            )}
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Supplier (Optional)</label>
                            <Select
                                options={supplierOptions}
                                isClearable
                                onChange={(selected) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        supplier_id: selected?.value || ''
                                    }));
                                }}
                                placeholder="Select supplier (or leave blank for self-mfg)"
                                isDisabled={submitting}
                                value={supplierOptions.find(opt => opt.value === formData.supplier_id)}
                            />
                        </div>
                        <div className="col-md-3 mb-3">
                            <label className="form-label">Purchase Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={formData.purchase_date}
                                onChange={e => setFormData(p => ({ ...p, purchase_date: e.target.value }))}
                            />
                        </div>
                        <div className="col-md-3 mb-3">
                            <label className="form-label">Default GST %</label>
                            <input
                                type="number"
                                className="form-control"
                                min="0"
                                max="100"

                                placeholder="e.g. 18"
                                value={taxPercent}
                                onChange={e => setTaxPercent(e.target.value)}
                            />
                            <small className="text-muted">Blank = use company default</small>
                        </div>
                        <div className="col-md-12">
                            <label className="form-label">Notes</label>
                            <textarea
                                className="form-control"
                                rows="2"
                                value={formData.notes}
                                onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                                placeholder="Additional notes for purchase order"
                            />
                        </div>
                    </div>

                    {/* Editable Items Table */}
                    {editableItems.length > 0 && (
                        <div className="border-top pt-3">
                            <h6 className="fw-bold mb-3">
                                Finished Goods — Edit quantities, prices & GST if needed
                            </h6>
                            {errors.items && <div className="alert alert-danger py-2">{errors.items}</div>}
                            <div className="table-responsive">
                                <table className="table table-bordered table-sm">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Product</th>
                                            <th>SKU</th>
                                            <th style={{ width: 100 }}>Qty</th>
                                            <th style={{ width: 120 }}>Unit Price (₹)</th>
                                            <th style={{ width: 100 }}>Discount (₹)</th>
                                            <th style={{ width: 90 }}>GST %</th>
                                            <th>Tax (₹)</th>
                                            <th>Total (₹)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {computedItems.map((item, i) => (
                                            <tr key={i}>
                                                <td><strong>{item.product_name}</strong></td>
                                                <td><code>{item.sku}</code></td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        min="0"
                                                        step="1"
                                                        value={item.quantity}
                                                        onChange={e => updateItem(i, 'quantity', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        min="0"

                                                        value={item.unit_price}
                                                        onChange={e => updateItem(i, 'unit_price', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        min="0"

                                                        value={item.discount}
                                                        onChange={e => updateItem(i, 'discount', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        min="0"
                                                        max="100"

                                                        placeholder={taxPercent || 'Co. default'}
                                                        value={item.tax_percent}
                                                        onChange={e => updateItem(i, 'tax_percent', e.target.value)}
                                                    />
                                                </td>
                                                <td className="text-warning">{formatCurrency(item.line_tax)}</td>
                                                <td className="fw-bold text-success">{formatCurrency(item.line_total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="table-light">
                                        <tr>
                                            <td colSpan="6" className="text-end fw-bold">Subtotal:</td>
                                            <td className="fw-bold">{formatCurrency(subtotal)}</td>
                                            <td />
                                        </tr>
                                        <tr>
                                            <td colSpan="6" className="text-end">Total GST:</td>
                                            <td className="text-warning">{formatCurrency(totalTax)}</td>
                                            <td />
                                        </tr>
                                        <tr>
                                            <td colSpan="6" className="text-end fw-bold">Grand Total:</td>
                                            <td colSpan="2" className="fw-bold text-primary fs-6">
                                                {formatCurrency(grandTotal)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}

                    {editableItems.length === 0 && selectedProduction && (
                        <div className="alert alert-warning mt-3">
                            <AlertCircle size={16} className="me-2" />
                            No finished goods found for this production. Please check the production details.
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { resetForm(); setShowConvertModal(false); }}>
                        Cancel
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleConvert}
                        disabled={submitting || !selectedProduction || editableItems.length === 0}
                    >
                        {submitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Converting...
                            </>
                        ) : (
                            <>
                                <ShoppingBag size={16} className="me-2" />
                                Convert to Purchase
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* View Production Modal */}
            <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <Package size={18} className="me-2" />
                        Production Details — {selectedProduction?.production_no}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedProduction && (
                        <>
                            <div className="row mb-4">
                                <div className="col-md-6">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <h6>Production Information</h6>
                                            <p><strong>No:</strong> {selectedProduction.production_no}</p>
                                            <p><strong>Batch:</strong> <code>{selectedProduction.batch_no}</code></p>
                                            <p><strong>Date:</strong> {formatDate(selectedProduction.production_date)}</p>
                                            <p><strong>Status:</strong> {getStatusBadge(selectedProduction.status)}</p>
                                            {selectedProduction.notes && <p><strong>Notes:</strong> {selectedProduction.notes}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <h6>Cost Summary</h6>
                                            <p><strong>Raw Materials:</strong> {formatCurrency(selectedProduction.total_raw_material_cost)}</p>
                                            <p><strong>Production Cost:</strong> {formatCurrency(selectedProduction.total_production_cost)}</p>
                                            <p><strong>Wastage:</strong> {formatCurrency(selectedProduction.total_wastage_cost)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {selectedProduction.finished_goods?.length > 0 && (
                                <>
                                    <h6 className="fw-bold mb-3">Finished Goods</h6>
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-sm">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Product</th>
                                                    <th>SKU</th>
                                                    <th>Qty</th>
                                                    <th>Unit Cost</th>
                                                    <th>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedProduction.finished_goods.map((g, i) => (
                                                    <tr key={i}>
                                                        <td>{g.product_name}</td>
                                                        <td><code>{g.sku}</code></td>
                                                        <td>{parseFloat(g.quantity_produced).toLocaleString('en-IN')} {g.unit}</td>
                                                        <td>{formatCurrency(g.unit_cost)}</td>
                                                        <td className="fw-bold">{formatCurrency(g.total_cost)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                            {selectedProduction.converted_to_purchase && (
                                <div className="alert alert-success mt-3">
                                    <CheckCircle size={16} className="me-2" />
                                    Converted: <strong>{selectedProduction.purchase_invoice_no}</strong>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowViewModal(false)}>Close</Button>
                    {canConvert && selectedProduction && !selectedProduction.converted_to_purchase && (
                        <Button
                            variant="success"
                            onClick={() => {
                                setShowViewModal(false);
                                openConvertForProduction(selectedProduction);
                            }}
                        >
                            <ShoppingBag size={16} className="me-2" />
                            Convert to Purchase
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ConvertToPurchase;